import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { questions, dimensions, calculateDimensionScore, getScoreLevel } from '@/data/questions';
import { practices, getRecommendedPractices } from '@/data/practices';
import { translations } from '@/data/translations';

const AppContext = createContext();

const STORAGE_KEYS = {
  ANSWERS: 'alzatuvoz_answers',
  COMMITMENTS: 'alzatuvoz_commitments',
  CURRENT_QUESTION: 'alzatuvoz_current_question',
  COMPLETED: 'alzatuvoz_completed',
  LANGUAGE: 'alzatuvoz_language'
};

export const AppProvider = ({ children }) => {
  // Language state
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    return saved || 'es';
  });

  // Questionnaire state
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ANSWERS);
    return saved ? JSON.parse(saved) : {};
  });

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CURRENT_QUESTION);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [isCompleted, setIsCompleted] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.COMPLETED);
    return saved === 'true';
  });

  // Commitments state (selected tips)
  const [commitments, setCommitments] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.COMMITMENTS);
    return saved ? JSON.parse(saved) : {};
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ANSWERS, JSON.stringify(answers));
  }, [answers]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CURRENT_QUESTION, currentQuestionIndex.toString());
  }, [currentQuestionIndex]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.COMPLETED, isCompleted.toString());
  }, [isCompleted]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.COMMITMENTS, JSON.stringify(commitments));
  }, [commitments]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
  }, [language]);

  // Translation helper
  const t = useCallback((key, replacements = {}) => {
    const keys = key.split('.');
    let result = translations[language];
    
    for (const k of keys) {
      if (result && result[k] !== undefined) {
        result = result[k];
      } else {
        return key;
      }
    }
    
    if (typeof result === 'string') {
      Object.entries(replacements).forEach(([placeholder, value]) => {
        result = result.replace(`{${placeholder}}`, value);
      });
    }
    
    return result;
  }, [language]);

  // Toggle language
  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'es' ? 'en' : 'es');
  }, []);

  // Answer a question
  const answerQuestion = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  // Navigate questions
  const goToNextQuestion = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentQuestionIndex]);

  const goToPreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  const goToQuestion = useCallback((index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
    }
  }, []);

  // Complete the questionnaire
  const completeQuestionnaire = useCallback(() => {
    setIsCompleted(true);
  }, []);

  // Reset everything
  const resetDiagnosis = useCallback(() => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setIsCompleted(false);
    setCommitments({});
    localStorage.removeItem(STORAGE_KEYS.ANSWERS);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_QUESTION);
    localStorage.removeItem(STORAGE_KEYS.COMPLETED);
    localStorage.removeItem(STORAGE_KEYS.COMMITMENTS);
  }, []);

  // Toggle a commitment (tip)
  const toggleCommitment = useCallback((practiceId, tipIndex) => {
    setCommitments(prev => {
      const practiceCommitments = prev[practiceId] || [];
      const isSelected = practiceCommitments.includes(tipIndex);
      
      if (isSelected) {
        return {
          ...prev,
          [practiceId]: practiceCommitments.filter(i => i !== tipIndex)
        };
      } else {
        return {
          ...prev,
          [practiceId]: [...practiceCommitments, tipIndex]
        };
      }
    });
  }, []);

  // Check if a tip is committed
  const isCommitted = useCallback((practiceId, tipIndex) => {
    return (commitments[practiceId] || []).includes(tipIndex);
  }, [commitments]);

  // Calculate scores
  const calculateScores = useCallback(() => {
    const dimensionScores = {};
    let totalScore = 0;
    let dimensionCount = 0;

    dimensions.forEach(dim => {
      const score = calculateDimensionScore(answers, dim.id);
      if (score > 0) {
        dimensionScores[dim.id] = score;
        totalScore += score;
        dimensionCount++;
      }
    });

    const overallScore = dimensionCount > 0 ? totalScore / dimensionCount : 0;

    return {
      dimensionScores,
      overallScore,
      dimensionDetails: dimensions.map(dim => ({
        ...dim,
        score: dimensionScores[dim.id] || 0,
        level: getScoreLevel(dimensionScores[dim.id] || 0)
      }))
    };
  }, [answers]);

  // Get recommended practices based on scores
  const getRecommendations = useCallback(() => {
    const { dimensionScores } = calculateScores();
    return getRecommendedPractices(dimensionScores);
  }, [calculateScores]);

  // Get all commitments as a flat list
  const getAllCommitments = useCallback(() => {
    const allCommitments = [];
    
    Object.entries(commitments).forEach(([practiceId, tipIndexes]) => {
      const practice = practices.find(p => p.id === practiceId);
      if (practice && tipIndexes.length > 0) {
        const tips = language === 'en' ? practice.tipsEn : practice.tips;
        tipIndexes.forEach(index => {
          if (tips[index]) {
            allCommitments.push({
              practiceId,
              practiceTitle: language === 'en' ? practice.titleEn : practice.title,
              dimension: practice.dimension,
              tip: tips[index],
              tipIndex: index
            });
          }
        });
      }
    });
    
    return allCommitments;
  }, [commitments, language]);

  // Check if has progress
  const hasProgress = Object.keys(answers).length > 0;

  // Current question
  const currentQuestion = questions[currentQuestionIndex];

  // Progress percentage
  const progressPercentage = (Object.keys(answers).length / questions.length) * 100;

  const value = {
    // Language
    language,
    toggleLanguage,
    t,
    
    // Questions data
    questions,
    dimensions,
    practices,
    
    // Questionnaire state
    answers,
    currentQuestionIndex,
    currentQuestion,
    isCompleted,
    hasProgress,
    progressPercentage,
    
    // Questionnaire actions
    answerQuestion,
    goToNextQuestion,
    goToPreviousQuestion,
    goToQuestion,
    completeQuestionnaire,
    resetDiagnosis,
    
    // Results
    calculateScores,
    getRecommendations,
    
    // Commitments
    commitments,
    toggleCommitment,
    isCommitted,
    getAllCommitments
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
